import type {
  CandleChunkDto,
  ChartEventPageDto,
  DatasetDetailDto,
  DatasetImportResultDto,
  DatasetSummaryDto,
  TimeframeCapabilitiesDto,
} from './types';
import { api } from './client';

export interface CandleRequest {
  timeframe?: string;
  duration?: string;
  direction: 'forward' | 'backward';
  limit: number;
  cursor?: string | null;
  from?: string | null;
  to?: string | null;
}

export interface EventRequest {
  limit: number;
  cursor?: string | null;
  from?: string | null;
  to?: string | null;
  impact?: string;
  currency?: string;
}

export class DatasetsService {
  async timeframeCapabilities(datasetId?: string, signal?: AbortSignal): Promise<TimeframeCapabilitiesDto> {
    const query = datasetId ? `?datasetId=${encodeURIComponent(datasetId)}` : '';
    return api.get<TimeframeCapabilitiesDto>(`/api/timeframe-capabilities${query}`, { signal });
  }

  async list(signal?: AbortSignal): Promise<DatasetSummaryDto[]> {
    return api.get<DatasetSummaryDto[]>('/api/datasets', { signal });
  }

  async get(id: string, signal?: AbortSignal): Promise<DatasetDetailDto> {
    return api.get<DatasetDetailDto>(`/api/datasets/${encodeURIComponent(id)}`, { signal });
  }

  async create(formData: FormData): Promise<DatasetImportResultDto> {
    return api.post<DatasetImportResultDto>('/api/datasets', formData);
  }

  async update(id: string, formData: FormData): Promise<DatasetImportResultDto> {
    return api.put<DatasetImportResultDto>(`/api/datasets/${encodeURIComponent(id)}`, formData);
  }

  async delete(id: string): Promise<void> {
    await api.delete<void>(`/api/datasets/${encodeURIComponent(id)}`);
  }

  async fetchCandles(id: string, request: CandleRequest, signal?: AbortSignal): Promise<CandleChunkDto> {
    const params = new URLSearchParams();
    if (request.timeframe) params.set('timeframe', request.timeframe);
    if (request.duration) params.set('duration', request.duration);
    params.set('direction', request.direction);
    params.set('limit', String(request.limit));
    if (request.cursor) params.set('cursor', request.cursor);
    if (request.from) params.set('from', request.from);
    if (request.to) params.set('to', request.to);

    return api.get<CandleChunkDto>(
      `/api/datasets/${encodeURIComponent(id)}/candles?${params}`,
      { signal },
    );
  }

  async fetchEvents(id: string, request: EventRequest, signal?: AbortSignal): Promise<ChartEventPageDto> {
    const params = new URLSearchParams({ limit: String(request.limit) });
    if (request.cursor) params.set('cursor', request.cursor);
    if (request.from) params.set('from', request.from);
    if (request.to) params.set('to', request.to);
    if (request.impact && request.impact !== 'All') params.set('impact', request.impact);
    if (request.currency?.trim()) params.set('currency', request.currency.trim().toUpperCase());

    return api.get<ChartEventPageDto>(
      `/api/datasets/${encodeURIComponent(id)}/events?${params}`,
      { signal },
    );
  }

  candleUrl(id: string, request: CandleRequest): string {
    const params = new URLSearchParams();
    if (request.timeframe) params.set('timeframe', request.timeframe);
    if (request.duration) params.set('duration', request.duration);
    params.set('direction', request.direction);
    params.set('limit', String(request.limit));
    if (request.cursor) params.set('cursor', request.cursor);
    if (request.from) params.set('from', request.from);
    if (request.to) params.set('to', request.to);
    return `/api/datasets/${encodeURIComponent(id)}/candles?${params}`;
  }

  eventsUrl(id: string, request: EventRequest): string {
    const params = new URLSearchParams({ limit: String(request.limit) });
    if (request.cursor) params.set('cursor', request.cursor);
    if (request.from) params.set('from', request.from);
    if (request.to) params.set('to', request.to);
    if (request.impact && request.impact !== 'All') params.set('impact', request.impact);
    if (request.currency?.trim()) params.set('currency', request.currency.trim().toUpperCase());
    return `/api/datasets/${encodeURIComponent(id)}/events?${params}`;
  }

  candlesFileUrl(id: string): string {
    return `/api/datasets/${encodeURIComponent(id)}/files/candles`;
  }

  newsFileUrl(id: string): string {
    return `/api/datasets/${encodeURIComponent(id)}/files/news`;
  }

}

export const datasets = new DatasetsService();
